import React from 'react';
import { PortvilleUsageRecord } from '../../../../types/portvilleBilling';
import { PortvilleRoomWithResidency } from '../../../../types/portvilleRoom';

interface UsageTableProps {
  usageRecords: PortvilleUsageRecord[];
  activeRooms: PortvilleRoomWithResidency[];
  loading: boolean;
  error: string | null;
  onEdit: (record: PortvilleUsageRecord) => void;
  onDelete: (recordId: string) => void;
}

const UsageTable: React.FC<UsageTableProps> = ({
  usageRecords,
  activeRooms,
  loading,
  error,
  onEdit,
  onDelete
}) => {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">사용량 데이터를 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="text-red-600 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-red-600 font-medium">사용량 데이터를 불러오는데 실패했습니다</p>
        <p className="text-gray-600 mt-1">{error}</p>
      </div>
    );
  }

  if (usageRecords.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <p className="text-gray-600 font-medium">사용량 기록이 없습니다</p>
        <p className="text-gray-500 mt-1">사용량 입력 버튼을 클릭하여 사용량을 입력해주세요</p>
      </div>
    );
  }

  // 사용량 기록을 호실번호 순으로 정렬
  const sortedRecords = [...usageRecords].sort((a, b) => {
    const roomNumberA = parseInt(a.roomNumber) || 0;
    const roomNumberB = parseInt(b.roomNumber) || 0;
    return roomNumberA - roomNumberB;
  });

  // 입력되지 않은 호실 목록
  const recordedRoomIds = new Set(usageRecords.map(record => record.roomId));
  const unrecordedRooms = activeRooms.filter(room => !recordedRoomIds.has(room.id));

  return (
    <div className="overflow-hidden">
      {/* 테이블 헤더 */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            사용량 기록 ({usageRecords.length}개)
          </h3>
          {unrecordedRooms.length > 0 && (
            <div className="text-sm text-orange-600">
              미입력 호실: {unrecordedRooms.length}개
            </div>
          )}
        </div>
      </div>

      {/* 테이블 */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                호실
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                입주자/담당자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                개별 전기 사용량 (kWh)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                개별 수도 사용량 (톤)
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                입력일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                입력자
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                액션
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedRecords.map((record) => {
              const room = activeRooms.find(r => r.id === record.roomId);
              return (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {record.roomNumber}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {room?.roomType === '시설물' ? (
                      <>
                        <div className="text-sm text-gray-900">
                          {room?.facilityInfo?.facilityName || '시설물'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {room?.facilityInfo?.responsiblePerson?.name || '-'} ({room?.facilityInfo?.facilityType || '기타'})
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-gray-900">
                          {room?.currentResident?.name || '입주자 없음'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {room?.currentResident?.organization || '-'}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {record.electricityIndividualUsage !== undefined && record.electricityIndividualUsage !== null
                        ? `${record.electricityIndividualUsage.toLocaleString()} kWh`
                        : '-'
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {record.waterIndividualUsage !== undefined && record.waterIndividualUsage !== null
                        ? `${record.waterIndividualUsage.toLocaleString()} 톤`
                        : '-'
                      }
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {record.recordedAt ? new Date(record.recordedAt).toLocaleDateString('ko-KR') : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {record.recordedBy || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => onEdit(record)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => onDelete(record.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 미입력 호실 목록 (있는 경우) */}
      {unrecordedRooms.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-orange-50">
          <h4 className="text-sm font-medium text-orange-800 mb-2">
            사용량 미입력 호실/시설물 ({unrecordedRooms.length}개)
          </h4>
          <div className="flex flex-wrap gap-2">
            {unrecordedRooms.map(room => (
              <span
                key={room.id}
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
              >
                {room.roomNumber}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageTable;
